import * as THREE from "three";
import { buildBackpack, buildGun, buildWasher } from "./rider/pack";
import { buildBoard } from "./rider/board";
import { buildHead } from "./rider/head";
import { buildArm, buildLeg } from "./rider/limbs";
import { DEFAULT_LOOK, FOOT_Z, HEAD_Y, NECK_BASE, NECK_TOP, type Arm, type Leg, type SkaterLook } from "./rider/look";
import { sharedWeave, skinMat } from "./rider/materials";
import { TORSOS } from "./rider/torso";
import { activeStyle } from "./styles";
import { inked } from "./toon";
import { facets } from "./rider/geom";

export { DEFAULT_LOOK, FOREARM, SHIN, THIGH, UPPER_ARM, type Arm, type Leg, type SkaterLook } from "./rider/look";

// Builds the rider's meshes from a `SkaterLook`: a chunky, big-headed rider
// built from primitives. `SkaterModel` (skater-model.ts) poses the rig every
// frame. Model space: the board's nose points +z; the rider stands regular
// (left foot forward) so they face -x. Each body region is built in
// `render/rider/` (#75: look, materials, feet, limbs, head, torso, outfit, board);
// this file puts them together in a fixed child order.

/** The rider's groups and limbs, for `SkaterModel` to pose. */
export interface SkaterRig {
  root: THREE.Group;
  /** Tips to the ground normal. */
  tilt: THREE.Group;
  /** Turns to the heading. */
  yawGroup: THREE.Group;
  /** Carries the board: raised for a grab tuck, pulled to a grabbing hand. */
  board: THREE.Group;
  /** The board's meshes, flipped and spun for tricks. */
  boardSpin: THREE.Group;
  rider: THREE.Group;
  hips: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  armFront: Arm;
  armBack: Arm;
  legs: Leg[];
  /** The paintball gun (#136), in torso space; hidden unless it's the item out. */
  gun: THREE.Group;
  /** The power washer's wand and its tank (#151), in torso space; hidden unless it's the item out. */
  washer: { wand: THREE.Group; tank: THREE.Group };
}

/**
 * Frees a rider built by `buildRider`: its geometries, materials and textures,
 * except the cloth weaves every rider shares.
 */
export function disposeRider(root: THREE.Object3D): void {
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.geometry.dispose();
    for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
      if (m instanceof THREE.MeshStandardMaterial) {
        for (const t of [m.map, m.normalMap, m.roughnessMap]) if (t && !sharedWeave(t)) t.dispose();
      }
      // The shared outline materials stay (they're cached by thickness).
      if (o.name !== "outline") m.dispose();
    }
  });
}

/**
 * Builds the whole rider and board from a look, in rest pose. Children are
 * added in a fixed order (`skater-pose.test.ts` keys joints by child index).
 */
export function buildRider(look: SkaterLook = DEFAULT_LOOK): SkaterRig {
  // The active style's proportions (#75): the cartoon's is the neutral toy build.
  const build = activeStyle().rider;
  const root = new THREE.Group();
  const tilt = new THREE.Group();
  const yawGroup = new THREE.Group();
  root.add(tilt);
  tilt.add(yawGroup);

  const board = new THREE.Group();
  const boardSpin = buildBoard(look);
  boardSpin.name = "board";
  board.position.y = 0.1;
  board.add(boardSpin);
  yawGroup.add(board);

  const rider = new THREE.Group();
  const hips = new THREE.Group();
  rider.position.y = 0.12;
  yawGroup.add(rider);
  rider.add(hips);

  const narrow = look.build === "narrow" ? 0.9 : 1;
  const kind = TORSOS[build.torso];
  hips.add(kind.pelvis(look, narrow));

  const torso = new THREE.Group();
  torso.position.y = 0.05;
  hips.add(torso);
  torso.add(kind.chest(look, narrow, build));
  // A short neck shows between the collar and the jaw (#80), slimmer toward the head.
  const neckGeo = new THREE.CylinderGeometry(0.05 * build.limbs, 0.062 * build.limbs, NECK_TOP - NECK_BASE, facets(16));
  neckGeo.translate(0, (NECK_TOP + NECK_BASE) / 2, 0);
  const neck = inked(neckGeo, skinMat(look.skin), 0.01);
  torso.add(neck);

  const head = new THREE.Group();
  // A smaller head sits lower, so its jaw still meets the neck.
  head.position.y = HEAD_Y - 0.16 * (1 - build.head);
  torso.add(head);
  buildHead(look, head, build);

  const armFront = buildArm(look, "front", torso, build);
  const armBack = buildArm(look, "back", torso, build);

  const legs = [buildLeg(look, FOOT_Z, build), buildLeg(look, -FOOT_Z, build)];
  for (const leg of legs) hips.add(leg.hip);

  // Extras go after the rig's own children, so the joints keep their child indexes.
  kind.extras(look, torso, hips, narrow);
  // The backpack and the paintball gun it holds (#136), after the outfit's extras.
  torso.add(buildBackpack(look));
  const gun = buildGun();
  torso.add(gun);
  // The power washer (#151) after them, so they keep their indexes.
  const washer = buildWasher();
  torso.add(washer.wand, washer.tank);
  return { root, tilt, yawGroup, board, boardSpin, rider, hips, torso, head, armFront, armBack, legs, gun, washer };
}
